import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeEuro, Clock4, SearchCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Seo } from "@/lib/seo";
import { SectionHeading, Reveal, FestgeldStatusBadge, ProcessTimeline, CTASection, ComplianceNote, FAQSection } from "@/components/shared/Shared";
import { useContent } from "@/context/ContentContext";

export default function Festgeld() {
  const { t } = useLang();
  const hero = useContent("festgeld.hero");
  const faqContent = useContent("faq.festgeld");
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const facts = [
    { icon: SearchCheck, title: t("Keine \u00f6ffentlichen Konditionen", "No public conditions"), text: t("Konditionen werden nicht pauschal ver\u00f6ffentlicht, sondern individuell kommuniziert.", "Conditions are not published across the board, but communicated individually.") },
    { icon: Clock4, title: t("Laufzeit", "Term"), text: t("Laufzeiten und Rahmenbedingungen h\u00e4ngen von der jeweiligen Verf\u00fcgbarkeit ab.", "Terms and frameworks depend on the respective availability.") },
    { icon: BadgeEuro, title: t("Anlagebetrag", "Investment amount"), text: t("Der m\u00f6gliche Rahmen wird im Rahmen der individuellen Pr\u00fcfung besprochen.", "The possible range is discussed as part of the individual review.") },
    { icon: ShieldCheck, title: t("Produktgeber", "Product provider"), text: t("Die Auswahl geeigneter Produktgeber erfolgt sorgf\u00e4ltig und dokumentiert.", "The selection of suitable product providers is careful and documented.") },
  ];

  const steps = [
    { title: t("Anfrage stellen", "Submit inquiry"), text: t("Sie teilen uns Betrag, Laufzeitpr\u00e4ferenz und Zeitrahmen mit \u2013 unverbindlich.", "You share amount, term preference and timeframe \u2013 without obligation.") },
    { title: t("Verf\u00fcgbarkeit pr\u00fcfen", "Check availability"), text: t("Wir pr\u00fcfen, ob aktuell passende M\u00f6glichkeiten bestehen.", "We check whether suitable options currently exist.") },
    { title: t("Individuelle Pr\u00fcfung", "Individual review"), text: t("Eignung, Laufzeit und Rahmenbedingungen werden pers\u00f6nlich besprochen.", "Suitability, term and framework are discussed personally.") },
    { title: t("R\u00fcckmeldung", "Response"), text: t("Sie erhalten eine dokumentierte, pers\u00f6nliche R\u00fcckmeldung \u2013 auch wenn aktuell nichts verf\u00fcgbar ist.", "You receive a documented, personal response \u2013 even if nothing is currently available.") },
  ];

  const faqDefaults = [
    { q: t("Warum sehe ich keine Zinss\u00e4tze?", "Why don't I see interest rates?"), a: t("Konditionen h\u00e4ngen von Verf\u00fcgbarkeit, Betrag und individueller Eignung ab. Wir kommunizieren sie erst nach pers\u00f6nlicher Pr\u00fcfung \u2013 das geh\u00f6rt zu unserem Qualit\u00e4tsverst\u00e4ndnis.", "Conditions depend on availability, amount and individual suitability. We communicate them only after a personal review \u2013 that is part of our quality standard.") },
    { q: t("Wie schnell erhalte ich eine R\u00fcckmeldung?", "How quickly will I get a response?"), a: t("In der Regel innerhalb weniger Werktage nach Eingang Ihrer Anfrage.", "Usually within a few business days after receiving your inquiry.") },
    { q: t("Bin ich mit einer Anfrage etwas verpflichtet?", "Does an inquiry commit me to anything?"), a: t("Nein. Ihre Anfrage ist kostenfrei und unverbindlich \u2013 sie stellt kein Angebot und keine Zusage dar.", "No. Your inquiry is free and non-binding \u2013 it does not constitute an offer or a commitment.") },
  ];
  const faq = (faqContent.items || []).length ? faqContent.items : faqDefaults;

  return (
    <div>
      <Seo
        title={t("Festgeld auf Anfrage \u2013 individuell gepr\u00fcft", "Fixed-Term on Request \u2013 Individually Reviewed")}
        description={t(
          "Festgeldkonditionen werden bei Eleven Family Office nicht \u00f6ffentlich angezeigt: individuelle Pr\u00fcfung von Verf\u00fcgbarkeit, Laufzeit und Rahmenbedingungen. Jetzt unverbindlich anfragen.",
          "Eleven Family Office does not display fixed-term conditions publicly: individual review of availability, term and framework. Inquire now without obligation."
        )}
      />
      {/* HERO */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <SectionHeading
                eyebrow={hero.eyebrow || t("Festgeld auf Anfrage", "Fixed-Term on Request")}
                title={hero.headline || t("Festgeldkonditionen \u2013 individuell gepr\u00fcft statt pauschal beworben", "Fixed-term conditions \u2013 individually reviewed instead of publicly advertised")}
                subtitle={hero.subline || t(
                  "Festgeldkonditionen werden bei Eleven Family Office nicht pauschal ver\u00f6ffentlicht. Bei Verf\u00fcgbarkeit pr\u00fcfen wir individuell, welche Konditionen, Laufzeiten und Rahmenbedingungen f\u00fcr Ihre Anfrage relevant sein k\u00f6nnen.",
                  "Eleven Family Office does not publish blanket fixed-term conditions. When available, we individually review which conditions, terms and frameworks may be relevant for your inquiry."
                )}
              />
              <Button asChild size="lg" className="mt-8 rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="festgeld-cta">
                <Link to="/anfrage/festgeld">{t("Festgeldverf\u00fcgbarkeit pr\u00fcfen", "Check fixed-term availability")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            </div>
            <Reveal>
              <div className="rounded-[8px] border border-border bg-[#F6F7F8] p-8" data-testid="festgeld-status-section">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280]">{t("Aktueller Status", "Current status")}</p>
                <div className="mt-3">
                  <FestgeldStatusBadge status={settings?.festgeld_status || "in_pruefung"} size="lg" />
                </div>
                <div className="mt-6 space-y-3 text-sm text-[#3B4452]">
                  {[
                    t("Keine \u00f6ffentliche Konditionsanzeige", "No public rate disclosure"),
                    t("Kommunikation nur nach individueller Pr\u00fcfung", "Communication only after individual review"),
                    t("Eine Anfrage stellt kein Angebot dar", "An inquiry does not constitute an offer"),
                  ].map((line) => (
                    <p key={line} className="flex items-start gap-2.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#FF3C20] shrink-0 mt-1.5" />
                      <span className="leading-relaxed">{line}</span>
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FACTS */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={t("Rahmenbedingungen", "Framework")} title={t("Was Sie wissen sollten", "What you should know")} />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {facts.map((f) => (
              <Reveal key={f.title} className="rounded-[8px] bg-white border border-border p-7 card-hover">
                <f.icon className="h-5 w-5 text-[#E8241B]" />
                <h4 className="mt-4 text-base font-semibold text-[#111318]">{f.title}</h4>
                <p className="mt-2 text-sm text-[#3B4452] leading-relaxed">{f.text}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 max-w-3xl">
            <ComplianceNote>
              {t(
                "Eine Anfrage stellt kein verbindliches Angebot dar. Konditionen k\u00f6nnen sich \u00e4ndern und werden ausschlie\u00dflich nach individueller Pr\u00fcfung kommuniziert.",
                "An inquiry does not constitute a binding offer. Conditions may change and are communicated exclusively after individual review."
              )}
            </ComplianceNote>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={t("Ablauf", "Process")} title={t("Vier Schritte bis zur pers\u00f6nlichen R\u00fcckmeldung", "Four steps to a personal response")} />
          <div className="mt-12"><ProcessTimeline steps={steps} /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="FAQ" title={t("Transparenz zu unserem Vorgehen", "Transparency about our approach")} />
          <div className="mt-10"><FAQSection items={faq} /></div>
        </div>
      </section>

      <CTASection
        title={t("Verf\u00fcgbarkeit unverbindlich pr\u00fcfen lassen.", "Have availability checked without obligation.")}
        subtitle={t("Ihre Anfrage ist kostenfrei. Wir melden uns pers\u00f6nlich mit dem Ergebnis der Pr\u00fcfung.", "Your inquiry is free of charge. We will contact you personally with the result of the review.")}
        primaryLabel={t("Verf\u00fcgbarkeit pr\u00fcfen", "Check availability")}
        primaryTo="/anfrage/festgeld"
      />
    </div>
  );
}
